import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { TreeViewBaseItem } from "@mui/x-tree-view";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  useHierarchicalSelection,
  SelectionRules,
  SELECTION_PRESETS,
} from "./hooks/useHierarchicalSelection";
import { TreeSearchInput } from "./TreeSearchInput/TreeSearchInput";
import { TreeView } from "./TreeView/TreeView";
import { getExpandedItemIds, searchItems } from "./utils";

interface TreeSelectProps {
  items: TreeViewBaseItem[];
  selectedItems?: string[];
  onSelectItems?: (selectedItems: string[]) => void;
  placeholder?: string;
  minTextLength?: number;
  noOptionsMessage?: string;
  allExpanded?: boolean;
  id?: string;
  onClose?: () => void;
  selectionRules?: SelectionRules;
  loading?: boolean;
  showItemCounts?: boolean;
  showDoneButton?: boolean;
  checkboxTestId?: string;
}

export const TreeSelect: React.FC<TreeSelectProps> = ({
  items,
  selectedItems = [],
  onSelectItems,
  placeholder = "Search...",
  noOptionsMessage = "No results found",
  minTextLength = 0,
  allExpanded = false,
  id = "tree-select",
  onClose,
  selectionRules = SELECTION_PRESETS.hierarchical,
  loading = false,
  showItemCounts = false,
  showDoneButton = false,
  checkboxTestId,
}) => {
  const {
    selectedItems: internalSelectedItems,
    displayItems,
    handleSelectionChange,
    handleItemDelete,
    isItemDisabled,
    getItemLabel,
  } = useHierarchicalSelection(
    items,
    selectedItems,
    onSelectItems,
    selectionRules
  );

  const [searchValue, setSearchValue] = useState("");
  const [initialExpandedState, setInitialExpandedState] = useState<string[]>(
    []
  );

  // Set initial expanded state if allExpanded is true
  useEffect(() => {
    if (allExpanded) {
      setInitialExpandedState(getExpandedItemIds(items));
    }
  }, [allExpanded, items]);

  const handleSelectItems = useCallback(
    (newValue: string[]) => {
      handleSelectionChange(newValue);
      if (newValue.length === 0) {
        setSearchValue("");
      }
    },
    [handleSelectionChange]
  );

  useEffect(() => {
    if (internalSelectedItems.length === 0) {
      setSearchValue("");
    }
  }, [internalSelectedItems]);

  // Filter items based on search input using the recursive search function
  const filteredItems = useMemo(
    () => (searchValue.trim() === "" ? items : searchItems(items, searchValue)),
    [items, searchValue]
  );

  // Get expanded item IDs based on search results or initial state
  const expandedItems = useMemo(() => {
    if (searchValue.trim() !== "") {
      return getExpandedItemIds(filteredItems);
    }

    return initialExpandedState;
  }, [filteredItems, searchValue, initialExpandedState]);

  const handleClear = useCallback(() => {
    handleSelectItems([]);
    setSearchValue("");
  }, [handleSelectItems]);

  const handleInputChange = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const renderTreeView = useMemo(() => {
    if (filteredItems.length === 0) {
      return (
        <Box sx={{ p: 2 }} id={`${id}-no-results`}>
          <Typography variant="body2" color="text.secondary">
            {noOptionsMessage}
          </Typography>
        </Box>
      );
    }

    return (
      <TreeView
        id={`${id}-tree-view`}
        items={filteredItems}
        selectedItems={internalSelectedItems}
        expandedItems={expandedItems}
        isItemDisabled={isItemDisabled}
        onSelectionChange={handleSelectItems}
        limitPopoverHeight
        selectionRules={{
          ...selectionRules,
          autoSelectParents: !searchValue.trim(),
          showIndeterminateCheckbox: !searchValue.trim(),
        }}
        showItemCounts={showItemCounts}
        checkboxTestId={checkboxTestId}
      />
    );
  }, [
    filteredItems,
    internalSelectedItems,
    handleSelectItems,
    isItemDisabled,
    noOptionsMessage,
    expandedItems,
    id,
    selectionRules,
    showItemCounts,
    searchValue,
    checkboxTestId,
  ]);

  return (
    <TreeSearchInput
      id={id}
      selectedItems={displayItems}
      value={searchValue}
      placeholder={placeholder}
      clear={handleClear}
      onInputChange={handleInputChange}
      renderPopperContent={renderTreeView}
      minCharsToShowPopper={minTextLength}
      onChipDelete={handleItemDelete}
      getItemLabel={getItemLabel}
      onClose={onClose}
      loading={loading}
      showDoneButton={showDoneButton}
    />
  );
};

export default TreeSelect;
